import { useState } from 'react'

import {
  EVENT_LEDGERS,
  useBatch,
  useConfirmLoad,
  useConfirmUnload,
  useAllHistory,
  useLoads,
  useRecipe,
  useRecipeV2Summary,
  useRecoverBatch,
  useAllSamples,
  useSafetyState,
  useStopBatch,
  useUnloads,
} from '@ctcells/api'
import { dataSourceFor } from '@ctcells/core'
import { errorMessage, t } from '@ctcells/i18n'
import { useParams, useSearchParams } from 'react-router'

import { Button } from '../../atoms/Button.tsx'
import { useCancelAfterStop } from '../../shell/executionCancel.ts'
import { useConfirmSource } from '../../shell/surfaceContext.ts'
import { useStartBlockers } from '../../shell/useStartBlockers.ts'
import { useScreenAccess } from '../screenAccess.ts'
import { ScreenLoading, ScreenState } from '../ScreenState.tsx'

import { sampleGroupFacts } from '../recipes/recipeForm.ts'

import type { BatchListedField } from './batchListedFields.ts'
import { useBatchOwners } from './useBatchOwners.tsx'
import { BatchDetailScreen, type BatchDetailScreenProps } from './BatchDetailScreen.tsx'
import { EventLogDownload } from './EventLogDownload.tsx'
import { BatchEventLog } from './BatchEventLog.tsx'
import { newFormatDoc } from './batchRecipeDoc.ts'
import { DEMO_BATCH_DETAIL } from '../demoData.ts'
import { historyForBatch, traysForBatch } from './batchDetailModel.ts'
import { batchSampleHistory } from './batchSampleHistory.ts'
import type { LoadConfirmReport } from './loadConfirmReport.ts'
import {
  DISPOSITION_DECISIONS,
  DISPOSITION_OPEN,
  DISPOSITION_PARAM,
  refusedSampleIds,
  type DispositionChoice,
  type DispositionOutcome,
} from './dispositionModel.ts'
import { refusedResumeChecks } from './resumeRouteModel.ts'

/**
 * 작업 상세 컨테이너 — 실물 배선(2026-09-20).
 *
 * 🔴 **갈리는 층은 `dataSourceFor()` 하나다.** demo 경로는 네트워크 훅을 **부르지 않고**
 *    쓰기 콜백도 붙이지 않는다 — 목 화면의 버튼이 실서버로 나가면 안 된다(`BatchNewContainer` 선례).
 *
 * 🔴 **한 화면이 여섯 도메인을 쓴다.** 하나라도 목이면 값의 출처가 갈려 화면이 거짓말을 한다.
 *    | 카드      | 경로                                           |
 *    | --------- | ---------------------------------------------- |
 *    | 머리줄    | `GET /batches/{id}` + `GET /recipes`(이름)     |
 *    | 명령      | `GET /history?event_type=audit` → `batch:<id>` |
 *    | 적재·수거 | `GET /loads?batch_id=` · `GET /unloads?batch_id=` |
 *    | 시료 웰 짝 | `GET /recipes/{recipe_id}` → `tray_path.sample_groups`(부가 · 실패해도 화면은 선다) |
 *
 * 🔴 **낙관적 갱신이 없다.** 명령·확약은 전부 `createCommand({send, invalidates})` 를 거치고,
 *    성공 후 하는 일은 무효화뿐이다 — 상태는 서버가 다시 준 값에서 온다(DP-04).
 * 🔴 **주기 폴링이 없다.** 스냅샷 1회 + 구독 무효화가 전부다(DP-01).
 *
 * ⚠ 이벤트 원장(`event_type=event`)은 **부르지 않는다** — 이벤트 탭을 걷었다(eric 2026-09-30).
 *
 * ⚠ **역할 경계가 카드마다 다르다.** `/samples` · `/history` 는 **operator/admin
 *   한정**이고 viewer 는 403 이다. 그래서 **한 요청이 실패해도 화면 전체를 에러로 떨구지
 *   않는다** — 그 카드만 사유를 낸다(`LogsContainer` 와 같은 갈래). 배치 자체(`/batches/{id}`)는
 *   역할 무관이라 그것만 실패하면 화면을 못 그린다.
 *
 * 경계 다섯
 * | 로딩 | `useBatch` 는 화면 전체, 나머지는 표의 `emptyLabel` 이 「불러오는 중」 |
 * | 에러 | 조회 실패는 탭별 사유, 쓰기 실패는 `errorMessage()` 가 **그 자리에**  |
 * | 빈   | 표가 스스로 「기록 없음」을 그린다                                     |
 * | 권한 | `useScreenAccess()` → 숨기지 않고 비활성 + 사유                        |
 * | 끊김 | 같은 `access.controlDisabled`. `gate()` 가 1순위로 막는다              |
 */

export interface BatchDetailContainerProps {
  localizeTrayPoints?: boolean | undefined
  showEventLog?: boolean | undefined
  hideEventLogDownload?: boolean | undefined
  /** 보일 작업. 안 넘기면 라우트 파라미터(`:batchId`)다 — 작업 목록의 오른쪽 판이 넘긴다(eric 2026-09-22) */
  batchId?: string | undefined
  /** 「← 작업」 · 「← 이력」 */
  backLabel?: string | undefined
  onBack?: (() => void) | undefined
  /** 서랍 닫기(오른쪽 위 ×). 넘기면 돌아가기 줄 대신 이것이 선다 */
  onClose?: (() => void) | undefined
  /**
   * 이 상세를 **연 목록이 이미 보이는 칸**. 요약 탭에서 그 줄이 빠진다(eric 2026-09-23).
   * 🔴 컨테이너는 값을 만들지 않고 그대로 지나 보낸다 — 무엇이 겹치는지는 «연 라우트»가 안다.
   */
  listedFields?: readonly BatchListedField[] | undefined
  /** 멈춘 모듈 → 모듈 상세(`HaltFacts`). 🔴 «연 라우트»가 준다 — 표면마다 경로가 다르다(태블릿 · 장비 관점은 안 준다) */
  onOpenModule?: ((moduleId: string) => void) | undefined
}

export function BatchDetailContainer(props: BatchDetailContainerProps) {
  /*
   * 🔴 **여섯 도메인이 다 live 여야 실물 경로로 간다.** 하나라도 목이면 — 예컨대 배치는
   *    실물인데 시료가 목이면 — 「이 작업에 없는 시료」가 표에 뜬다. 섞지 않는다.
   */
  const live =
    dataSourceFor('batches') === 'live' &&
    dataSourceFor('samples') === 'live' &&
    dataSourceFor('recipes') === 'live' &&
    dataSourceFor('trays') === 'live' &&
    dataSourceFor('logs') === 'live'

  return live ? <BatchDetailLive {...props} /> : <BatchDetailDemo {...props} />
}

/** demo 는 쓰기 콜백을 **안 붙인다** — 화면이 「목 데이터에서는 보낼 수 없습니다」로 비활성한다 */
function BatchDetailDemo(props: BatchDetailContainerProps) {
  const access = useScreenAccess()
  const download = props.hideEventLogDownload ? null : (
    <EventLogDownload
      batch={DEMO_BATCH_DETAIL.batch}
      live={false}
      permissions={access.permissions}
      label={props.showEventLog ? t('action.eventLogDownloadShort') : undefined}
      ledgers={props.showEventLog ? EVENT_LEDGERS : undefined}
    />
  )
  return (
    <BatchDetailScreen
      access={access}
      eventLog={
        props.showEventLog ? (
          <BatchEventLog
            batchId={DEMO_BATCH_DETAIL.batch.batch_id}
            live={false}
            actions={download}
          />
        ) : null
      }
      /* 🔴 목 자료면 단추는 서되 비활성 + 사유다 — 목 화면이 실서버를 때리지 않는다 */
      headerActions={props.showEventLog ? null : download}
      {...clean(props)}
    />
  )
}

function BatchDetailLive(props: BatchDetailContainerProps) {
  const access = useScreenAccess()
  const { batchId: routeBatchId } = useParams()
  /* 작업 띠 안내 「이력에서 처리 →」로 들어오면 처분 창을 바로 연다(`dispositionHref`) — 처분 대기일 때만 화면이 연다 */
  const [search] = useSearchParams()
  const openDisposition = search.get(DISPOSITION_PARAM) === DISPOSITION_OPEN
  const batchId = props.batchId ?? routeBatchId
  const id = batchId ?? ''

  const batch = useBatch({ batchId })
  // 🔴 전부 받는다 — 이 작업의 시료는 목록 끝에 있기 쉽다(`useAllSamples` 머리말)
  const samples = useAllSamples({})
  const audits = useAllHistory({ batch_id: id, event_type: 'audit' })
  const loads = useLoads({ batch_id: id })
  const unloads = useUnloads({ batch_id: id })
  /* 비상정지 중인가 — 작업 줄과 같은 조회(같은 캐시 키 · 요청이 늘지 않는다). 「복구 › 재개」를 막는다(eric 2026-10-14) */
  const safety = useSafetyState({ skip: dataSourceFor('safety') !== 'live' })

  /*
   * 🔴 **일시정지 · 재개 · 중지 훅이 없다**(eric 2026-09-30 — 머리 단추 줄을 걷었다). 그 명령은 상시 작업 줄
   *    (`shell/BatchStripContainer`)이 보낸다. 여기 남은 쓰기는 복구 판정과 확약뿐이다.
   */
  const recover = useRecoverBatch()
  /*
   * 🔴 **대기 작업 취소만**(eric 2026-10-29) — 같은 `stop` 경로지만 화면이 시작 전 작업(`created` · `queued`)에만 단추를 세운다.
   *    실행 중 작업의 중지는 그대로 작업 줄 하나다. 시작 전이라 모듈 실행이 없어 `cancelAfter` 를 부르지 않는다
   *    (서버가 전이 뒤 `_cancel_batch_executions` 를 이미 돈다). 시료 해제는 서버가 같은 트랜잭션에서 하고,
   *    시료 캐시는 `batch_status` 구독 프레임이 무효화한다(`stream/bridge.ts`) — 폴링하지 않는다.
   */
  const stopBatch = useStopBatch()
  /* 🔴 폐기 뒤 모듈 실행 취소 — 작업 줄과 **같은 한 벌**이다(`shell/executionCancel.ts`). 실패 문구는 작업 줄에 선다 */
  const execCancel = useCancelAfterStop()
  const confirmLoad = useConfirmLoad()
  const confirmUnload = useConfirmUnload()
  /*
   * 🔴 **확약 창구는 셸이 선언한 표면에서 온다**(`surfaceContext.ts`). 이 컨테이너는 콘솔·태블릿
   *    둘 다에서 뜨지만 「내가 어디 있나」를 여기서 가리지 않는다. 미선언이면 `null` 이고,
   *    그때는 확약 버튼을 비활성 + 사유로 세운다 — 창구를 지어 보내지 않는다.
   */
  const confirmSource = useConfirmSource()

  /*
   * 🔴 **시료 표의 웰 짝(「A1, A2」)은 레시피 묶음이 정한다** — 모니터링과 같은 함수(`sampleGroupFacts`).
   *    **부가 조회다**: 못 받으면 웰 칸이 서버 배정 웰 하나로 선다. 화면을 게이트하지 않는다.
   */
  const recipe = useRecipe({ recipeId: batch.data?.recipe_id })
  /** 「대기」의 «왜» — 작업 줄과 같은 대조(`shell/useStartBlockers.ts`). 🔴 조기 반환보다 앞에서 부른다 */
  const startBlockers = useStartBlockers(batch.data)
  /** 작업자 이름표 — 🔴 admin 에게만 값이 있다(`useBatchOwners`). 없으면 「작업」 카드에 작업자 줄이 안 선다 */
  const owners = useBatchOwners()

  /*
   * 「레시피」 카드 [상세] 팝업(eric 2026-10-22) — 작업 추가 창과 같은 팝업 · 같은 요약 호출(`POST /recipes/summary` · 캐시 `<id>@<version>`).
   * 🔴 **팝업이 열렸을 때만 묻는다**(`skip`) — 서랍을 열 때마다 요약 요청이 나가지 않는다.
   * 🔴 **작업이 고정한 판만 보인다.** `GET /recipes/{id}` 는 레시피의 «지금 판»뿐이고 판을 고르는 경로가 없다 — 판이 다르면
   *    문서를 싣지 않고 그 사실을 적는다(다른 판의 본문을 이 작업의 레시피로 보이지 않는다).
   */
  const [recipeOpen, setRecipeOpen] = useState(false)
  const fixedVersion = batch.data?.recipe_version
  const sameVersion = recipe.data !== undefined && recipe.data.version === fixedVersion
  const recipeDoc = sameVersion ? newFormatDoc(recipe.data?.payload) : undefined
  const recipeSummary = useRecipeV2Summary({
    doc: recipeDoc,
    cacheId:
      recipe.data === undefined
        ? 'none'
        : `${recipe.data.recipe_id}@${String(recipe.data.version)}`,
    skip: !recipeOpen,
  })

  if (batch.isPending) return <ScreenLoading />
  /*
   * 🔴 **막다른 화면을 만들지 않는다**(2026-09-22). 상세를 못 받으면 본문이 통째로 사라지는데,
   *    종전에는 그 자리에 글자 한 줄뿐이라 «여기서 무엇을 하라는 것인지»가 없었다.
   *    돌아갈 줄은 이미 라우트가 `onBack`·`backLabel` 로 넘겨 준다 — 그것을 그대로 쓴다.
   * ⚠ 오래된 링크로 들어온 404 가 실제 경로다(`{"detail":"Not Found"}` — 계약 봉투 밖이라
   *   문구는 `errorMessage` 가 상태 코드로 고른다).
   */
  if (batch.isError) {
    return (
      <ScreenState
        actions={
          props.onBack === undefined ? undefined : (
            <Button onClick={props.onBack} size="md" variant="primary">
              {t('app.back')}
            </Button>
          )
        }
        text={errorMessage(batch.error)}
        tone="error"
      />
    )
  }

  const historical = batch.data.status === 'aborted' || batch.data.status === 'completed'
  const membership = historical
    ? batchSampleHistory(batch.data, samples.data?.items ?? [], audits.data?.items ?? [])
    : undefined
  const mine = (samples.data?.items ?? []).filter((sample) =>
    membership === undefined
      ? sample.batch_id === id
      : membership.sampleIds.includes(sample.sample_id),
  )

  /** 🔴 **명령 실패 한 줄** — 이 화면이 보내는 상태변경은 복구 판정과 대기 작업 취소 둘이다 */
  const commandError = recover.error ?? stopBatch.error
  const confirmError = [confirmLoad, confirmUnload].find((m) => m.error !== null)?.error

  /**
   * 🔴 **카드가 비어 있는 «사유»를 카드마다 따로 나른다.** 「없다」와 「못 봤다」는 다른 사실이다.
   *
   * viewer 는 `/samples`(`samples.py:81 READ_ROLES`)와 `/history`
   * (`history.py PRIVILEGED_ROLES`)가 **둘 다 403** 인데 `/batches/{id}` 는 200 이라 화면이
   * 멀쩡히 선다 — 사유가 없으면 그 두 카드가 「시료 없음」·「기록 없음」으로 **거짓을 단언**한다.
   * ⚠ 위의 `loading`(네 조회 OR)은 그대로 둔다. 사유가 그보다 **먼저** 서므로
   *   「적재 조회가 아직 안 끝났다」가 「시료를 못 본 이유」를 덮지 않는다.
   */
  const noticeOf = (query: { isError: boolean; error: unknown }): string | undefined =>
    query.isError ? errorMessage(query.error as Parameters<typeof errorMessage>[0]) : undefined

  const download = props.hideEventLogDownload ? null : (
    <EventLogDownload
      batch={batch.data}
      live
      permissions={access.permissions}
      label={props.showEventLog ? t('action.eventLogDownloadShort') : undefined}
      ledgers={props.showEventLog ? EVENT_LEDGERS : undefined}
    />
  )

  return (
    <BatchDetailScreen
      {...clean(props)}
      loadDefaultsEnabled
      eventLog={props.showEventLog ? <BatchEventLog batchId={id} live actions={download} /> : null}
      {...(owners === undefined ? {} : { owners })}
      access={access}
      /*
       * 🔴 **이벤트 로그 내려받기**(eric 2026-10-02) — 상태 무관 한 단추. 실행 중이면 누른 시각까지다.
       *    이벤트 원장만(`event_type=event`). `/history` 는 operator · admin 한정이라 viewer 에게는
       *    비활성 + 사유다(요청을 안 보낸다).
       */
      headerActions={props.showEventLog ? null : download}
      onRecipeDetail={setRecipeOpen}
      recipeDetail={{
        recipeId: batch.data.recipe_id,
        loading: recipe.isPending || (recipeDoc !== undefined && recipeSummary.isPending),
        doc: recipeDoc,
        summary: recipeSummary.data,
        notice: recipe.isError
          ? noticeOf(recipe)
          : recipe.data !== undefined && !sameVersion
            ? t('batchDetail.recipe.versionGone', {
                fixed: batch.data.recipe_version,
                current: recipe.data.version,
              })
            : noticeOf(recipeSummary),
      }}
      confirming={confirmLoad.isPending || confirmUnload.isPending}
      commandPending={recover.isPending || stopBatch.isPending}
      onCancel={(cancelReason) => stopBatch.mutateAsync({ batchId: id, reason: cancelReason })}
      additionalResumeChecks={refusedResumeChecks(recover.error)}
      estopped={safety.data?.state === 'emergency_stop'}
      startBlockers={startBlockers}
      data={{
        batch: batch.data,
        /*
         * 🔴 **응답 칸이다**(`Batch.recipe_name` · 2026-09-26). 종전의 `/recipes` 별도 조회를 걷었다.
         *    `null`(레시피 행을 못 찾음)이면 추적할 수 있게 id 로 떨어뜨린다.
         */
        recipeName: batch.data.recipe_name ?? batch.data.recipe_id,
        /* 🔴 부가 조회 — 받은 레시피의 판이 작업의 판과 같을 때만(다르면 그 판의 등록 시각이 아니다) */
        ...(recipe.data !== undefined && recipe.data.version === batch.data.recipe_version
          ? { recipeCreatedAt: recipe.data.created_at }
          : {}),
        samples: mine,
        sampleHistory: membership,
        /* 🔴 부가 조회다 — 못 받았거나 `payload` 가 없으면(목 · 옛 응답) 묶음 0, 웰 칸은 서버 배정 웰 하나로 선다 */
        sampleGroups: sampleGroupFacts(sameVersion ? recipe.data?.payload : undefined).groups,
        commands: historyForBatch(audits.data?.items, id),
        load: traysForBatch(loads.data?.items, id),
        unload: traysForBatch(unloads.data?.items, id),
      }}
      loading={samples.isPending || audits.isPending || loads.isPending || unloads.isPending}
      notices={{
        samples: noticeOf(samples),
        sampleHistory: historical
          ? (noticeOf(audits) ??
            (membership?.incomplete
              ? t('batchHistory.incomplete')
              : membership?.sampleIds.some(
                    (sampleId) => !mine.some((sample) => sample.sample_id === sampleId),
                  )
                ? t('batchHistory.currentMissing')
                : undefined))
          : undefined,
        commands: noticeOf(audits),
      }}
      {...(commandError == null ? {} : { commandError: errorMessage(commandError) })}
      {...(confirmError == null ? {} : { confirmError: errorMessage(confirmError) })}
      /*
       * 🔴 확약 본문 = `command_id`(훅이 짓는다) + `confirm_source`(표면)
       *    + 적재만 **신고 칸**(`loaded_chambers` · 선택). 신고는 화면의 적재 확약 창이 받고,
       *    비운 칸은 `report` 에 키가 없다 — 그대로 훅에 펼친다(「비우면 신고하지 않는다」).
       *    ⚠ 평형추 신고(`counterweight_loaded`)는 2026-09-29 계약에서 빠졌다. 모듈 409 문구는
       *    `confirmError` 로 창 안에 선다.
       * 🔴 적재는 **`mutateAsync`** 다 — 돌려준 약속이 「창을 닫아도 되는가」다(중단 대화와 같은 규칙).
       */
      {...(confirmSource === null
        ? { confirmUnavailableReason: t('reason.confirmSurfaceUnknown') }
        : {
            onConfirmLoad: (loadId: string, report: LoadConfirmReport) =>
              confirmLoad.mutateAsync({ loadId, confirmSource, ...report }),
            onConfirmUnload: (unloadId: string) => {
              confirmUnload.mutate({ unloadId, confirmSource })
            },
          })}
      /*
       * ⚠ `resume` 만 사유 없이 선다. 나머지 둘은 화면이 대화로 받아 넘긴다.
       * 🔴 **`mutateAsync` 를 쓴다** — 돌려준 약속이 화면의 「대화를 닫아도 되는가」다.
       *    `mutate`(void) 로 두면 성공해도 대화가 안 닫힌다(2026-09-20 실물 검증에서 걸렸다).
       *    ⚠ 거절은 화면이 받아 삼킨다. 실패 문구는 `commandError` 로 따로 흐른다.
       */
      initialDisposition={openDisposition}
      /*
       * 🔴 **시료 처분 — 결정마다 요청 하나**(`discard_samples` · `rerun_samples` · 사유 공통 · 관리자 페이지 `sendDispositions` 와 같은 차례).
       *    한 요청이 422 로 거절되면 그 요청은 아무것도 안 바꿨다(계약) — `context.sample_ids` 를 그 줄의 사유로 돌려준다.
       *    다른 결정 요청은 따로 보낸다(먼저 성공한 것은 «처리됨»). 성공은 서버가 `review_reason` 을 내려 복구 줄이 사라지는 것으로 보인다.
       */
      onDispose={async (requests, disposeReason) => {
        const outcome: DispositionOutcome = { done: [], refused: [], errors: [] }
        for (const decision of DISPOSITION_DECISIONS) {
          const sampleIds = requests[decision]
          if (sampleIds.length === 0) continue
          try {
            await recover.mutateAsync({ batchId: id, decision, reason: disposeReason, sampleIds })
            outcome.done.push(...sampleIds)
          } catch (error) {
            outcome.refused.push(
              ...refusedSampleIds(error as Parameters<typeof refusedSampleIds>[0]),
            )
            outcome.errors.push(
              t('disposition.failed', {
                decision: t(DECISION_LABEL[decision]),
                message: errorMessage(error as Parameters<typeof errorMessage>[0]),
              }),
            )
          }
        }
        return outcome
      }}
      onRecover={(decision, recoverReason, opts) =>
        recover
          .mutateAsync({
            batchId: id,
            decision,
            ...(recoverReason === undefined || recoverReason === ''
              ? {}
              : { reason: recoverReason }),
            // 확인 재개 · 시간 원자 방식(`ResumeRouteModal`) — 대화가 채운 칸만 싣는다
            ...(opts?.physicalChecked === undefined
              ? {}
              : { physicalChecked: opts.physicalChecked }),
            ...(opts?.physicalChecks === undefined ? {} : { physicalChecks: opts.physicalChecks }),
            ...(opts?.timedAtom === undefined ? {} : { timedAtom: opts.timedAtom }),
          })
          .then((accepted) => {
            // 🔴 **폐기만** 작업을 끝낸다 — 재개·완료 확정은 모듈을 건드리지 않는다
            if (decision === 'discard') void execCancel.cancelAfter(id, recoverReason)
            return accepted
          })
      }
    />
  )
}

/** 처분 실패 문구의 결정 이름 */
const DECISION_LABEL = {
  discard_samples: 'disposition.discard',
  rerun_samples: 'disposition.rerun',
} as const satisfies Record<DispositionChoice, string>

/** `exactOptionalPropertyTypes` — `undefined` 인 키를 아예 안 넘긴다 */
function clean(props: BatchDetailContainerProps): Partial<BatchDetailScreenProps> {
  return {
    ...(props.localizeTrayPoints === undefined
      ? {}
      : { localizeTrayPoints: props.localizeTrayPoints }),
    ...(props.backLabel === undefined ? {} : { backLabel: props.backLabel }),
    ...(props.onBack === undefined ? {} : { onBack: props.onBack }),
    ...(props.onClose === undefined ? {} : { onClose: props.onClose }),
    ...(props.listedFields === undefined ? {} : { listedFields: props.listedFields }),
    ...(props.onOpenModule === undefined ? {} : { onOpenModule: props.onOpenModule }),
  }
}
